import { useState } from 'react';
import { useIntl } from 'react-intl';

import { FormField } from '~/eds/src/components/FormField/FormField';
import { useModal } from '~/eds/src/components/hooks/useModal';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { api } from '~/redux/api/createApi';
import { useRouting } from '~/routing/useRouting';

type Props = {
  disabled?: boolean;
  playbookId: string;
  playbookName: string;
};

export const Delete = ({ disabled, playbookId, playbookName }: Props) => {
  const intl = useIntl();
  const { toast } = useToast();
  const { navigate } = useRouting();
  const [deleteInput, setDeleteInput] = useState('');

  const [deletePlaybook, { isLoading: isDeletingPlaybook }] =
    api.endpoints.deletePlaybook.useMutation();

  const [confirmDelete, showConfirmDelete, hideConfirmDelete] = useModal({
    title: intl.formatMessage(
      {
        id: 'playbooks.details.delete.confirmTitle',
        defaultMessage: 'Delete {playbook}',
        description:
          'Title for delete playbook confirmation modal. E.g Delete NDA',
      },
      {
        playbook: playbookName,
      },
    ),
    primaryAction: {
      text: intl.formatMessage({
        id: 'playbooks.details.delete.confirmPrimaryAction',
        defaultMessage: 'Delete',
        description:
          'Label for primary action button in delete playbook confirmation modal',
      }),
      onClick: async () => {
        try {
          await deletePlaybook({ playbookId });
          hideConfirmDelete();
          navigate('/playbooks');
        } catch {
          toast({
            status: 'danger',
            title: intl.formatMessage({
              id: 'playbooks.details.delete.errorToastTitle',
              defaultMessage: 'Error Deleting Playbook',
              description:
                'Title for toast notification when deleting a playbook fails',
            }),
            message: intl.formatMessage({
              id: 'playbooks.details.delete.errorToastMessage',
              defaultMessage:
                'There was an error deleting the playbook. Please try again.',
              description:
                'Message for toast notification when deleting a playbook fails',
            }),
          });
        }
      },
      isLoading: isDeletingPlaybook,
      disabled: deleteInput !== playbookName,
      variant: 'danger',
    },
    children: (
      <Layout direction="column" spacing={4}>
        <Text>
          {intl.formatMessage({
            id: 'playbooks.details.delete.confirmMessage',
            defaultMessage:
              'This action will permanently delete the selected playbook and all its contents. You will not be able to recover it later. Type the playbook name below to confirm.',
            description: 'Message in delete playbook confirmation modal',
          })}
        </Text>
        <FormField
          input={TextInput}
          label={intl.formatMessage({
            id: 'playbooks.details.delete.confirmInputLabel',
            defaultMessage: 'Playbook Name',
            description: 'Label for input field to confirm playbook deletion',
          })}
          placeholder={intl.formatMessage({
            id: 'playbooks.details.delete.confirmInputPlaceholder',
            defaultMessage: 'Enter playbook name',
            description:
              'Placeholder for input field to confirm playbook deletion',
          })}
          name="confirmDeletePlaybook"
          onChange={(value) => setDeleteInput(value as string)}
          value={deleteInput}
        />
      </Layout>
    ),
  });

  return (
    <>
      <IconButton
        disabled={disabled}
        icon="trash"
        onClick={() => {
          showConfirmDelete();
        }}
      />
      {confirmDelete}
    </>
  );
};
